import React, { useState } from 'react';
import { TFunction } from 'react-i18next';
import { connect, ConnectedProps } from 'react-redux';
import moment from 'moment';
import { withInject } from '@piwikpro/platform';
import {
  Stack, Badge, Caption, Button, formatDate, formatToOptions, withTranslation, IconColor, Header, Skeleton,
} from '@piwikpro/ui-components';
import { MarketingCratePaymentsState } from '../../redux';
import ManagePaymentModal from '../ManagePaymentModal/ManagePaymentModal';

const mapStateToProps = (state: MarketingCratePaymentsState) => {
  const { details, isLoading: detailLoading } = state.payments.subscription;
  const { details: tierList, isLoading: tiersLoading } = state.payments.tiers;

  return {
    details: details as NonNullable<typeof details>,
    tiers: tierList as NonNullable<typeof tierList>,
    isLoading: detailLoading || tiersLoading,
  };
};

const connector = connect(mapStateToProps);

type ReduxProps = ConnectedProps<typeof connector>;

interface InjectedProps {
  i18n: import('i18next').i18n
}

interface Props extends ReduxProps, InjectedProps {
  t: TFunction
  hideManagePaymentButton?: Boolean
  hidePlanName?: boolean
}

export const PaymentBadge: React.FC<Props> = ({
  details,
  i18n,
  isLoading,
  t,
  hideManagePaymentButton,
  hidePlanName,
  tiers,
}) => {
  const [paymentManageModalOpened, setPaymentManageModalOpened] = useState(false);

  if (isLoading || !details) {
    return <Skeleton width="256px" height="24px" roundBorder />;
  }

  const getCurrentPlanName = () => {
    const currentPlanId = details.price_id;
    const currentPlan = tiers.find(
      ({ id }) => id === currentPlanId,
    );
    const traffic = currentPlan?.traffic;

    if (!traffic) {
      return '0.5M';
    }

    return `${(traffic / 1e6).toFixed(1).replace(/\.0$/, '') }M`;
  };

  const getBadgeLabel = () => {
    if (details.scheduled_change_action === 'cancel') return 'paddle.payment.current-subscription.badge.cancelled';
    return details.billing_cycle === 'month' ? 'paddle.payment.current-subscription.billing-cycle.monthly' : 'paddle.payment.current-subscription.billing-cycle.yearly';
  };

  return (
    <>
      <Stack verticalAlignment="center">
        {!hidePlanName && (
          <Stack.Item>
            <Header>
              {t('paddle.payment.current-subscription.core.text', { planName: getCurrentPlanName() })}
            </Header>
          </Stack.Item>
        )
        }
        <Stack.Item previousSpacing={hidePlanName ? 'default' : 'narrow'}>
          <Badge
            size="medium"
            color="white"
            text={t(getBadgeLabel())}
            iconLeft={{
              name: !details.scheduled_change_action ? 'refresh' : 'refresh-error',
              color: !details.scheduled_change_action ? 'green' : 'light-gray' as IconColor,
              size: 'small',
            }}
          />
        </Stack.Item>
        <Stack.Item previousSpacing="narrow" fill>
          <Caption subdued>
            {t(!details.scheduled_change_effective_at ? 'paddle.payment.current-subscription.next-payment' : 'paddle.payment.current-subscription.ends')}
            {' '}
            <span style={{ color: details.status === 'past_due' ? '#E3301B' : 'inherit' }}>
              {formatDate(
                moment(details.next_payment ? details.next_payment : details.scheduled_change_effective_at),
                i18n.language.split('-')[0],
                formatToOptions('LL'),
              )}
            </span>
          </Caption>
        </Stack.Item>
        {
          !hideManagePaymentButton && (
            <Stack.Item>
              <Button
                icon="external-link"
                text={t('paddle.payment.current-subscription.manage-payment.btn-text')}
                onClick={() => { setPaymentManageModalOpened(true); }}
              />
            </Stack.Item>
          )
        }
      </Stack>
      {paymentManageModalOpened && (
        <ManagePaymentModal onClose={() => { setPaymentManageModalOpened(false); }} />
      )}
    </>
  );
};

export default connector(withTranslation(['administration'])((PaymentBadge)));
